// src/utils/structureUtils.js
import { arrayMove } from '@dnd-kit/sortable';

/**
 * ストラクチャーの行配列を受け取り、指定されたインデックス間で要素を移動させ、
 * 'level' タイプの行のレベル番号を再計算する純粋関数。
 * @param {Array<object>} rows - 現在の行の配列
 * @param {number} oldIndex - ドラッグ元のインデックス
 * @param {number} newIndex - ドロップ先のインデックス
 * @returns {Array<object>} - 並べ替えられ、レベルが再計算された新しい行の配列
 */
export const reorderRows = (rows, oldIndex, newIndex) => {
  const newOrderedRows = arrayMove(rows, oldIndex, newIndex);

  // 'level' タイプの行のレベル番号を振り直す
  let levelCounter = 0;
  return newOrderedRows.map(row => {
    if (row.type === 'level' && row.level !== 'break') {
      levelCounter++;
      return { ...row, level: levelCounter };
    }
    return row;
  });
};

/**
 * 列（columns）配列を受け取り、指定されたIDに基づいて要素を移動させる純粋関数。
 * Level列が先頭、Time列が末尾であるというルールを維持する。
 * @param {Array<object>} columns - 現在の列の配列
 * @param {string} activeId - ドラッグされた要素のID
 * @param {string} overId - ドロップ先の要素のID
 * @param {{requiredLevelKey: string, requiredTimeKey: string}} appConfig - 必須キーを含む設定
 * @returns {Array<object>} - 並べ替え後の新しい列の配列。ルール違反の場合は元の配列を返す。
 */
export const reorderColumns = (columns, activeId, overId, appConfig) => {
  const oldIndex = columns.findIndex(c => c.id === activeId);
  let newIndex = columns.findIndex(c => c.id === overId);

  if (oldIndex === -1 || newIndex === -1) return columns;

  const { requiredLevelKey, requiredTimeKey } = appConfig;

  // 必須列（先頭と末尾）の位置を保護しつつ、その隣へのドロップを許容する
  if (newIndex === 0) newIndex = 1;
  if (newIndex === columns.length - 1) newIndex = columns.length - 2;

  const newOrderedColumns = arrayMove(columns, oldIndex, newIndex);

  // ルール検証：先頭がLevel列、末尾がTime列であるか
  if (
    newOrderedColumns[0]?.label === requiredLevelKey &&
    newOrderedColumns[newOrderedColumns.length - 1]?.label === requiredTimeKey
  ) {
    return newOrderedColumns;
  }
  return columns;
};

/**
 * デフォルトの列定義を生成する関数
 * @param {string} levelKey - レベルを表すキー
 * @param {string} timeKey - 時間を表すキー
 * @returns {Array<object>} - デフォルトの列定義の配列
 */
export const createDefaultColumns = (levelKey = 'level', timeKey = 'time') => [
  { id: `col-${levelKey}`, label: levelKey },
  { id: "col-SB", label: "SB" },
  { id: "col-BB", label: "BB" },
  { id: "col-Ante", label: "Ante" },
  { id: `col-${timeKey}`, label: timeKey },
];

/**
 * フォールバック用の初期行データを生成する関数
 * @returns {Array<object>} - 1行だけの初期レベルデータの配列
 */
export const createInitialRows = () => [
  { id: `row-fallback-${Date.now()}`, type: "level", level: 1, SB: 100, BB: 200, Ante: 0, time: "20:00" },
];